/**
 * CorrectionSearch — inline search popover for picking a different entity.
 * When the entity has a parent (edificio → cliente, impianto → edificio),
 * the search is always scoped to that parent: no free search allowed.
 * Keyboard: ↑↓ navigate, Enter select, Esc close.
 */
import { useState, useRef, useEffect } from "react";
import type { EntityTypeConfig, EntitySearchResult } from "../api/types";
import { searchEntities, fetchEntityChildren } from "../api/client";
import { stripPkPrefix } from "../helpers/entityUtils";

interface CorrectionSearchProps {
  entityType: EntityTypeConfig;
  currentPk?: string;
  /** PK of the current parent entity (cliente PK for edificio, edificio PK for impianto). */
  currentEdificioPk?: string;
  initialQuery?: string;
  onPick: (entity: EntitySearchResult) => void;
  onClose: () => void;
}

export function CorrectionSearch({
  entityType, currentPk, currentEdificioPk, initialQuery, onPick, onClose,
}: CorrectionSearchProps) {
  const [query, setQuery] = useState(initialQuery ?? "");
  const [results, setResults] = useState<EntitySearchResult[]>([]);
  const [siblings, setSiblings] = useState<EntitySearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [sel, setSel] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  // Scoped whenever this entity type has a parent and a parent PK is available.
  const hasParentScope = !!(entityType.parent_entity && currentEdificioPk);

  useEffect(() => { inputRef.current?.focus(); }, []);

  // Pre-load all siblings under the current parent.
  useEffect(() => {
    if (!hasParentScope || !currentEdificioPk || !entityType.parent_entity) return;
    (async () => {
      try {
        const children = await fetchEntityChildren(entityType.parent_entity!, currentEdificioPk, entityType.key);
        setSiblings(children.filter((c) => c.pk !== currentPk));
      } catch { /* ignore */ }
    })();
  }, [hasParentScope, currentEdificioPk, entityType.parent_entity, entityType.key, currentPk]);

  // Debounced search.
  // When scoped and siblings are loaded: client-side filter (no server call).
  // When scoped but siblings not yet loaded, or no scope: server search with optional parent_pk.
  useEffect(() => {
    clearTimeout(timerRef.current);
    const q = query.trim();
    if (q.length < 2) { setResults([]); return; }

    if (hasParentScope && siblings.length > 0) {
      // Client-side filter on pre-loaded list — instant, no debounce needed.
      const ql = q.toLowerCase();
      setResults(
        siblings.filter((r) =>
          (r.label ?? r.pk).toLowerCase().includes(ql) ||
          Object.values(r.fields ?? {}).some((v) => String(v ?? "").toLowerCase().includes(ql))
        )
      );
      setSel(0);
      return;
    }

    timerRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const scopePk = hasParentScope ? currentEdificioPk : undefined;
        const res = await searchEntities(entityType.key, q, 12, scopePk);
        setResults(res.filter((r) => r.pk !== currentPk));
        setSel(0);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 250);

    return () => clearTimeout(timerRef.current);
  }, [query, entityType.key, currentPk, currentEdificioPk, hasParentScope, siblings]);

  // Show siblings when no search query (scoped), search results when typing.
  const displayResults = query.trim().length >= 2 ? results : (hasParentScope ? siblings : []);
  const showingPreloaded = query.trim().length < 2 && displayResults.length > 0;

  function onKey(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown") { e.preventDefault(); setSel((s) => Math.min(s + 1, displayResults.length - 1)); }
    if (e.key === "ArrowUp") { e.preventDefault(); setSel((s) => Math.max(s - 1, 0)); }
    if (e.key === "Enter" && displayResults[sel]) { e.preventDefault(); onPick(displayResults[sel]); }
    if (e.key === "Escape") onClose();
  }

  // Human-readable scope label for the badge.
  const scopeLabel = hasParentScope
    ? entityType.key === "impianto"
      ? "Impianti di questo edificio"
      : `${entityType.label}i di questo cliente`
    : null;

  return (
    <div onClick={(e) => e.stopPropagation()} className="flow-dropdown" style={{ marginTop: 10, padding: 10 }}>
      {/* Search input */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <span className="material-symbols-outlined" style={{ fontSize: 16, color: "var(--indaco-gray-500)" }}>search</span>
        <input ref={inputRef} value={query}
          onChange={(e) => { setQuery(e.target.value); setSel(0); }}
          onKeyDown={onKey}
          placeholder={
            hasParentScope
              ? `Cerca tra i ${entityType.label.toLowerCase()}i del ${entityType.key === "impianto" ? "edificio" : "cliente"} selezionato…`
              : `Cerca ${entityType.label.toLowerCase()} per nome, via o codice…`
          }
          style={{
            flex: 1, border: 0, outline: 0, fontFamily: "var(--font-family)",
            fontSize: 13, padding: "4px 0", color: "var(--indaco-gray-900)",
          }} />
        {loading && <span className="spinner" style={{ width: 14, height: 14 }} />}
        <button className="btn btn-outline btn-sm" onClick={onClose}>
          <span className="material-symbols-outlined" style={{ fontSize: 13 }}>close</span>
          Chiudi
        </button>
      </div>

      {/* Scope badge — shown when search is restricted to a parent entity */}
      {scopeLabel && (
        <div style={{
          display: "flex", alignItems: "center", gap: 6, padding: "4px 8px", marginBottom: 6,
          background: "var(--indaco-brand-primary-light)",
          border: "1px solid var(--indaco-brand-primary)",
          borderRadius: 6, opacity: 0.9,
        }}>
          <span className="material-symbols-outlined" style={{ fontSize: 13, color: "var(--indaco-brand-primary)" }}>filter_alt</span>
          <span style={{ fontSize: 11, color: "var(--indaco-brand-primary)", fontWeight: 600 }}>{scopeLabel}</span>
          <code style={{ fontSize: 10, color: "var(--indaco-brand-primary)", marginLeft: "auto" }}>{currentEdificioPk}</code>
        </div>
      )}

      {/* Results */}
      <div style={{ borderTop: "1px solid var(--indaco-gray-200)", paddingTop: 4, maxHeight: 260, overflowY: "auto" }}>
        {showingPreloaded && (
          <div style={{ padding: "4px 8px 6px", fontSize: 11, color: "var(--indaco-gray-500)", display: "flex", alignItems: "center", gap: 4 }}>
            <span className="material-symbols-outlined" style={{ fontSize: 13 }}>{entityType.icon ?? "list"}</span>
            {siblings.length} {entityType.label.toLowerCase()}{siblings.length !== 1 ? "i" : ""} disponibili:
          </div>
        )}
        {displayResults.length === 0 && query.trim().length >= 2 && !loading ? (
          <div style={{ padding: "12px 8px", textAlign: "center", fontSize: 12, color: "var(--indaco-gray-600)" }}>
            {`Nessun risultato per "${query}"`}
          </div>
        ) : displayResults.map((r, i) => {
          const isEdificio = entityType.key === "edificio";
          const cc = isEdificio && r.fields?.codice_cliente ? String(r.fields.codice_cliente).trim() : "";
          const addr = isEdificio ? [r.fields?.indirizzo, r.fields?.comune].filter(Boolean).map(String).join(" · ") : "";
          return (
            <button key={r.pk} onMouseEnter={() => setSel(i)}
              onClick={() => onPick(r)}
              className="flow-dropdown-item"
              style={{
                background: sel === i ? "var(--indaco-brand-primary-light)" : "transparent",
                padding: "7px 8px", borderRadius: 6,
                display: "flex", alignItems: "center", gap: 8,
              }}>
              <span className="material-symbols-outlined" style={{ fontSize: 18, color: "var(--indaco-gray-600)" }}>{entityType.icon}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: "var(--indaco-gray-900)", display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.label || r.pk}</span>
                  {cc && (
                    <span style={{
                      fontSize: 10, fontWeight: 600, padding: "0 5px",
                      background: "var(--indaco-brand-primary-light)",
                      color: "var(--indaco-brand-primary)", borderRadius: 999,
                    }}>{cc}</span>
                  )}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 1 }}>
                  <code style={{ fontSize: 10, color: "var(--indaco-gray-500)" }}>{stripPkPrefix(r.pk)}</code>
                  {addr && <span style={{ fontSize: 10, color: "var(--indaco-gray-600)" }}>{addr}</span>}
                </div>
              </div>
              {sel === i && <span className="material-symbols-outlined" style={{ fontSize: 14, color: "var(--indaco-brand-primary)" }}>keyboard_return</span>}
            </button>
          );
        })}
        {query.trim().length < 2 && displayResults.length === 0 && (
          <div style={{ padding: "12px 8px", textAlign: "center", fontSize: 12, color: "var(--indaco-gray-500)" }}>
            {hasParentScope ? "Caricamento…" : "Digita almeno 2 caratteri per cercare"}
          </div>
        )}
      </div>
    </div>
  );
}
